import { useMemo, useState } from "react";
import { dramas } from "@/mocks/dramas";
import { genreTabs } from "@/mocks/site";
import DramaCard from "@/pages/home/components/DramaCard";
import SectionHeading from "@/pages/home/components/SectionHeading";
import Reveal from "@/components/feature/Reveal";

export default function Library() {
  const [keyword, setKeyword] = useState("");
  const [genre, setGenre] = useState("全部");

  const completed = useMemo(
    () => dramas.filter((d) => d.status === "已完结"),
    [],
  );

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return completed.filter((d) => {
      const matchGenre = genre === "全部" || d.genres.includes(genre);
      if (!matchGenre) return false;
      if (!kw) return true;
      const haystack = [d.title, ...d.genres, ...d.tags, d.synopsis]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    });
  }, [completed, keyword, genre]);

  const reset = () => {
    setKeyword("");
    setGenre("全部");
  };

  return (
    <section id="library" className="w-full bg-background-100 py-14 md:py-20">
      <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="Full Series Library"
          title="全集资源 · 一次看完"
          desc="这里收录已经完结、可以一口气看完的韩剧。支持关键词检索与题材筛选，搜索会同时匹配剧名、题材、标签与简介，帮你在这份现有片单里快速定位。"
        />

        <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative w-full lg:max-w-sm">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none">
                <i className="ri-search-line text-base" aria-hidden="true" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索剧名、题材或标签，如「复仇」"
                aria-label="搜索全集资源"
                className="w-full pl-10 pr-4 py-2.5 rounded-md text-sm font-label bg-background-100 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
              />
            </div>

            <div className="flex flex-wrap gap-2 flex-1">
              {genreTabs.map((tab) => {
                const selected = tab === genre;
                return (
                  <button
                    key={tab}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setGenre(tab)}
                    className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                      selected
                        ? "bg-secondary-600 text-background-50"
                        : "bg-background-200/70 text-foreground-700 hover:text-secondary-800 hover:bg-secondary-100"
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 text-sm font-label text-foreground-500">
            <span>
              找到 <span className="text-primary-700 font-medium">{list.length}</span> 部全集剧集
            </span>
            {keyword || genre !== "全部" ? (
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置筛选
              </button>
            ) : null}
          </div>
        </div>

        {list.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {list.map((drama, index) => (
              <Reveal key={drama.id} delay={(index % 3) * 70}>
                <DramaCard drama={drama} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
            <span className="w-12 h-12 mx-auto rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-search-eye-line text-xl text-foreground-500" aria-hidden="true" />
            </span>
            <p className="mt-3 text-sm font-label text-foreground-600">
              没有匹配「{keyword}」的全集剧集，试试其他关键词或重置筛选。
            </p>
          </div>
        )}
      </div>
    </section>
  );
}